export class HelpView {
	constructor(state, options = {}) {
		this.state = state;
		this.options = options;
		this.element = null;
		this._unsubscribe = null;
	}

	render() {
		this.element = document.createElement('div');
		this.element.className = 'liveChat-view liveChat-help-view';

		this._updateContent();

		this._unsubscribe = this.state.subscribe((type) => {
			if (type === 'helpArticlesUpdate' || type === 'helpSearchChange') {
				this._updateCollectionsList();
			}
		});

		return this.element;
	}

	_updateContent() {
		const searchQuery = this.state.helpSearchQuery || '';

		this.element.innerHTML = `
			<div class="liveChat-help-header">
				<div class="liveChat-help-header-top">
					<h2>Help</h2>
					<button class="sdk-close-btn liveChat-help-close-btn liveChat-mobile-close-btn" aria-label="Close">
						<iconify-icon icon="ph:x" width="18" height="18"></iconify-icon>
					</button>
				</div>
				<div class="liveChat-help-search-wrap">
					<span class="liveChat-help-search-icon">
						<iconify-icon icon="ph:magnifying-glass" width="16" height="16"></iconify-icon>
					</span>
					<input
						type="text"
						class="liveChat-help-search-input"
						placeholder="Search for help..."
						value="${searchQuery}"
					/>
				</div>
			</div>
			<div class="liveChat-help-body">
				<div class="liveChat-help-collections"></div>
			</div>
		`;

		this._updateCollectionsList();
		this._attachEvents();
	}

	_updateCollectionsList() {
		const container = this.element.querySelector('.liveChat-help-collections');
		const collections = (this.state.helpArticles || []).filter(
			(c) => (c.articleCount || 0) > 0
		);
		const searchQuery = (this.state.helpSearchQuery || '').toLowerCase();

		const filtered = searchQuery
			? collections.filter(
					(c) =>
						c.title.toLowerCase().includes(searchQuery) ||
						(c.description && c.description.toLowerCase().includes(searchQuery))
				)
			: collections;

		if (filtered.length === 0) {
			container.innerHTML = this._renderEmptyState();
			return;
		}

		container.innerHTML = filtered
			.map((c) => this._renderCollectionItem(c))
			.join('');
		this._attachCollectionEvents();
	}

	_avatarColors = [
		{ bg: '#EF4444', text: '#FFFFFF' },
		{ bg: '#F97316', text: '#FFFFFF' },
		{ bg: '#F59E0B', text: '#FFFFFF' },
		{ bg: '#10B981', text: '#FFFFFF' },
		{ bg: '#06B6D4', text: '#FFFFFF' },
		{ bg: '#3B82F6', text: '#FFFFFF' },
		{ bg: '#8B5CF6', text: '#FFFFFF' },
		{ bg: '#EC4899', text: '#FFFFFF' },
	];

	_getAvatarColor(id) {
		const hash = id
			.split('')
			.reduce((acc, char) => acc + char.charCodeAt(0), 0);
		return this._avatarColors[hash % this._avatarColors.length];
	}

	_getInitials(name) {
		if (!name) return 'A';
		return name
			.split(' ')
			.map((n) => n[0])
			.join('')
			.toUpperCase()
			.slice(0, 2);
	}

	_renderAuthorAvatar(collection) {
		if (collection.author?.picture) {
			return `<img
				src="${collection.author.picture}"
				alt="${collection.author.name || ''}"
				class="liveChat-help-collection-avatar"
				title="${collection.author.name || ''}"
			/>`;
		}

		const { bg, text } = this._getAvatarColor(collection.id);
		const initials = collection.author?.name
			? this._getInitials(collection.author.name)
			: 'A';

		return `<span
			class="liveChat-help-collection-avatar liveChat-help-collection-avatar--initials"
			style="background-color: ${bg}; color: ${text};"
			title="${collection.author?.name || 'Author'}"
		>${initials}</span>`;
	}

	_resolveCollectionIcon(icon) {
		if (!icon) return this._defaultCollectionIcon();

		if (icon.trimStart().startsWith('<')) {
			return `<span class="liveChat-help-collection-icon">${icon}</span>`;
		}

		if (icon.startsWith('ph:')) {
			return `<span class="liveChat-help-collection-icon">
				<iconify-icon icon="${icon}"></iconify-icon>
			</span>`;
		}

		return this._defaultCollectionIcon();
	}

	_defaultCollectionIcon() {
		return `<span class="liveChat-help-collection-icon">
			<iconify-icon icon="ph:book-open"></iconify-icon>
		</span>`;
	}

	_renderCollectionItem(collection) {
		const articleCount = collection.articleCount || 0;

		return `
			<div class="liveChat-help-collection" role="button" tabindex="0" data-collection-id="${collection.id}">
				${this._resolveCollectionIcon(collection.icon)}
				<div class="liveChat-help-collection-content">
					<div class="liveChat-help-collection-title">${collection.title}</div>
					${collection.description ? `<p class="liveChat-help-collection-desc">${collection.description}</p>` : ''}
					<div class="liveChat-help-collection-meta">
						${this._renderAuthorAvatar(collection)}
						<span class="liveChat-help-collection-count">
							${articleCount} ${articleCount === 1 ? 'article' : 'articles'}
						</span>
					</div>
				</div>
			</div>
		`;
	}

	_renderEmptyState() {
		if (this.state.helpSearchQuery) {
			return `
				<div class="liveChat-empty-state">
					<div class="liveChat-empty-state-icon">
						<iconify-icon icon="ph:magnifying-glass" width="48" height="48"></iconify-icon>
					</div>
					<h3>No results found</h3>
					<p>Try a different search term</p>
				</div>
			`;
		}

		return `
			<div class="liveChat-empty-state">
				<div class="liveChat-empty-state-icon">
					<iconify-icon icon="ph:books" width="48" height="48"></iconify-icon>
				</div>
				<h3>Help collections</h3>
				<p>No collections available yet</p>
			</div>
		`;
	}

	_attachEvents() {
		const mobileCloseBtn = this.element.querySelector(
			'.liveChat-help-close-btn'
		);
		if (mobileCloseBtn) {
			mobileCloseBtn.addEventListener('click', () => {
				this.state.setOpen(false);
			});
		}

		const searchInput = this.element.querySelector(
			'.liveChat-help-search-input'
		);
		let searchTimeout;
		searchInput.addEventListener('input', (e) => {
			clearTimeout(searchTimeout);
			searchTimeout = setTimeout(() => {
				this.state.setHelpSearchQuery(e.target.value);
			}, 300);
		});

		this._attachCollectionEvents();
	}

	_attachCollectionEvents() {
		this.element
			.querySelectorAll('.liveChat-help-collection')
			.forEach((item) => {
				item.addEventListener('click', () => {
					const collection = this.state.helpArticles.find(
						(c) => c.id === item.dataset.collectionId
					);
					if (collection?.url) {
						window.open(collection.url, '_blank');
					} else if (this.options.onArticleClick) {
						this.options.onArticleClick(collection);
					}
				});
			});
	}

	destroy() {
		if (this._unsubscribe) this._unsubscribe();
		if (this.element?.parentNode)
			this.element.parentNode.removeChild(this.element);
	}
}
